import 'yet-another-react-lightbox/styles.css';
import 'yet-another-react-lightbox/plugins/thumbnails.css';

import { useRouter } from 'next/navigation';
import React, { CSSProperties, Fragment, useEffect, useRef, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import Lightbox from 'yet-another-react-lightbox';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';

import { InventoryImageSlider } from '@/components/Inventory/InventoryImageSlider';
import { getImageUrl, getUniqueInventoryImageUrl } from '@/tools/inventory';
import { isMobile } from '@/tools/views';
import { faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IInventory, InventoryField } from '@lamesarv-sdk/types';

const BIG_IMAGE_WIDTH = 600;
const BIG_IMAGE_HEIGHT = 500;

const aspectRatio = `${BIG_IMAGE_WIDTH} / ${BIG_IMAGE_HEIGHT}`;

enum ScrollState {
  Start,
  End,
  Middle,
}

const DefaultImage = ({
  item,
  defaultImageBackground,
}: {
  item: IInventory;
  defaultImageBackground?: string | null;
}) => (
  <div
    className="relative border-t border-gray-100 flex justify-center bg-black overflow-hidden"
    style={{ backgroundColor: defaultImageBackground || 'black' }}
  >
    <img
      alt={item[InventoryField.title]}
      src={getUniqueInventoryImageUrl(item)}
      className="bg-black object-cover relative"
      style={{ width: `${BIG_IMAGE_WIDTH / 16}rem`, aspectRatio }}
    />
  </div>
);

export interface IDetailsImagesProps {
  item: IInventory;
  defaultImageBackground?: string | null;
}

export const DetailsImages = ({ item, defaultImageBackground }: IDetailsImagesProps) => {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollState, setScrollState] = useState(ScrollState.Start);
  const [lightboxData, setLightboxData] = useState<{ isOpen: boolean; selectedIndex: number }>({
    isOpen: false,
    selectedIndex: 0,
  });
  const router = useRouter();

  useEffect(() => {
    if (!scrollRef.current) return;

    const handleScroll = () => {
      if (!scrollRef.current) return;

      const newIsStart = Math.floor(scrollRef.current.scrollLeft) === 0;

      if (newIsStart) return setScrollState(ScrollState.Start);

      const newIsEnd =
        Math.ceil(scrollRef.current.scrollLeft) + scrollRef.current.clientWidth === scrollRef.current.scrollWidth;

      if (newIsEnd) return setScrollState(ScrollState.End);

      setScrollState(ScrollState.Middle);
    };

    scrollRef.current.addEventListener('scroll', handleScroll);

    return () => {
      if (!scrollRef.current) return;

      scrollRef.current.removeEventListener('scroll', handleScroll);
    };
  }, []);

  if (!item[InventoryField.imageCloudinaryIds]?.length)
    return <DefaultImage item={item} defaultImageBackground={defaultImageBackground} />;

  const getScrollAmount = () => {
    if (!scrollRef.current) return 0;

    if (isMobile()) return window.innerWidth + 4;

    const secondImage = scrollRef.current.querySelector('div:nth-child(2)>img');

    if (!secondImage) return 0;

    const imageWidthWithGap = secondImage.clientWidth + 4;

    return imageWidthWithGap;
  };

  const scrollLeft = () => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({
        left: scrollRef.current.scrollLeft - getScrollAmount(),
        behavior: 'smooth',
      });
    }
  };

  const scrollRight = () => {
    if (!scrollRef.current) return;

    scrollRef.current.scrollTo({
      left: scrollState === ScrollState.End ? 0 : scrollRef.current.scrollLeft + getScrollAmount(),
      behavior: 'smooth',
    });
  };

  const showLeftArrow = scrollState !== ScrollState.Start;

  const handleSelectImage = (index: number) => {
    const isNotMobile = window.innerWidth >= 768;

    setLightboxData({ isOpen: isNotMobile, selectedIndex: index });
  };

  return (
    <Fragment>
      <div className="relative border-t border-gray-100 order-2">
        <button
          type="button"
          onClick={() => router.back()}
          className="md:hidden absolute left-4 top-4 z-10 bg-[#F0F9FC] rounded-full size-9 shadow-[0px_4px_4px_0px_#00000040] flex items-center justify-center hover:opacity-90"
        >
          <FontAwesomeIcon icon={faChevronLeft} className="size-4 text-[#0F0603]" />
        </button>

        {/* Images */}
        <div
          className="flex flex-col md:flex-row gap-1"
          style={
            {
              '--big-image-width': `${BIG_IMAGE_WIDTH / 16}rem`,
            } as CSSProperties
          }
        >
          <div className="bg-black w-full md:w-[var(--big-image-width)]" style={{ aspectRatio }}>
            <img
              src={getImageUrl(item[InventoryField.imageCloudinaryIds][0], BIG_IMAGE_WIDTH, BIG_IMAGE_HEIGHT)}
              className="h-full w-full object-cover cursor-pointer duration-100 hover:brightness-[.8] hidden md:block"
              alt=""
              onClick={() => handleSelectImage(0)}
            />

            <InventoryImageSlider
              className="md:hidden"
              height={BIG_IMAGE_HEIGHT}
              width={BIG_IMAGE_WIDTH}
              images={item[InventoryField.imageCloudinaryIds]}
              index={lightboxData.selectedIndex}
              onIndexChange={(index) => setLightboxData((prev) => ({ ...prev, selectedIndex: index }))}
              swipe
            />
          </div>
          <div className="relative min-w-[33.3%] flex-1">
            <div ref={scrollRef} className="md:h-full md:w-full overflow-x-auto no-scrollbar mx-2 md:mx-0 mt-2 md:mt-0">
              <div className="flex md:flex-col gap-x-1 md:flex-wrap h-full">
                <div
                  className={twMerge(
                    'size-[5.625rem] bg-black relative md:hidden',
                    lightboxData.selectedIndex === 0 && 'border-4 border-[#FFBC2B] md:border-0',
                  )}
                  style={{ aspectRatio }}
                >
                  <img
                    src={getImageUrl(item[InventoryField.imageCloudinaryIds][0], BIG_IMAGE_WIDTH, BIG_IMAGE_HEIGHT)}
                    className="absolute w-full h-full top-0 left-0 object-cover cursor-pointer duration-100 hover:brightness-[.8]"
                    alt=""
                    onClick={() => handleSelectImage(0)}
                  />
                </div>
                {item[InventoryField.imageCloudinaryIds].slice(1).map((publicId, index) => (
                  <div
                    key={index + 1}
                    className={twMerge(
                      'size-[5.625rem] md:size-auto md:h-[calc(50%-2px)] md:odd:mt-1 bg-black relative',
                      lightboxData.selectedIndex === index + 1 && 'border-4 border-[#FFBC2B] md:border-0',
                    )}
                    style={{ aspectRatio }}
                  >
                    <img
                      src={getImageUrl(publicId, BIG_IMAGE_WIDTH, BIG_IMAGE_HEIGHT)}
                      className="absolute w-full h-full top-0 left-0 object-cover cursor-pointer duration-100 hover:brightness-[.8]"
                      alt=""
                      onClick={() => handleSelectImage(index + 1)}
                    />
                  </div>
                ))}
              </div>
            </div>

            {showLeftArrow && (
              <div
                className="absolute left-0 top-1/2 -translate-y-1/2 py-4 rounded-r-lg bg-white bg-opacity-80 shadow-md cursor-pointer"
                onClick={scrollLeft}
              >
                <FontAwesomeIcon icon={faChevronLeft} className="h-8" />
              </div>
            )}
            <div
              className="absolute right-0 top-1/2 -translate-y-1/2 py-4 rounded-l-lg bg-white bg-opacity-80 shadow-md cursor-pointer"
              onClick={scrollRight}
            >
              <FontAwesomeIcon icon={faChevronRight} className="h-8" />
            </div>
          </div>
        </div>
      </div>
      <Lightbox
        open={lightboxData.isOpen}
        close={() => setLightboxData((prev) => ({ ...prev, isOpen: false }))}
        slides={item[InventoryField.imageCloudinaryIds].map((publicId) => ({
          src: getImageUrl(publicId, 900, 750, {
            width: 900,
            height: 750,
          }),
          width: 900,
          height: 750,
        }))}
        index={lightboxData.selectedIndex}
        animation={{
          swipe: 0,
        }}
        styles={{
          container: { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
        }}
        className="[&_img]:w-[900px] [&_img]:h-[750px]"
        plugins={[Thumbnails]}
        carousel={{
          preload: 10,
        }}
        thumbnails={{
          border: 0,
          gap: 20,
          width: 100,
          height: 100,
          padding: 0,
        }}
      />
    </Fragment>
  );
};
